import { isMobile } from 'react-device-detect';
import {
  Box,
  Button,
  Center,
  Group,
  Paper,
  Stack,
  Tabs,
  Text,
} from '@mantine/core';
import CollapsibleNav, {
  type CollapsibleNavItem,
} from '@components/CollapsibleNav/CollapsibleNav';
import CandidatesEmptyList from '@company/pages/JobOverview/DetailV2/CandidatesPanel/CandidatesEmptyList';
import SuggestedWorkersPaywall from '@company/pages/JobOverview/DetailV2/CandidatesPanel/SuggestedWorkersPaywall';
import CandidatesFilterBar from '@company/pages/JobOverview/DetailV2/CandidatesPanel/Filter/CandidatesFilterBar';
import CandidateCard from '@company/pages/JobOverview/DetailV2/CandidatesPanel/CandidateCard';
import { useCandidatesQuery } from '@company/hooks/jobDetailHooks/useCandidatesQuery';
import { useCandidateCounters } from '@company/hooks/jobDetailHooks/useCandidateCounters';
import {
  useJobDetailStore,
  useActiveCandidateFilters,
} from '@company/pages/JobOverview/DetailV2/stores/jobDetailStore';
import useJobDetailUrlSync from '@company/pages/JobOverview/DetailV2/hooks/useJobDetailUrlSync';
import {
  CANDIDATE_PANEL_TABS,
  resolveCandidatePanelEmpty,
  candidatePanelTabCount,
  candidatesGroupCount,
  type CandidatePanelTab,
} from '@company/helpers/job-overview-helper';
import { CandidateStage } from '@company/types/job-overview.type';
import { capitalizeValue } from '@utils/StringUtils';
import IJobDetail from '@th-types/job.detail.type';
import BullhornLoadingSpinner from '@components/BullhornLoadingSpinner/BullhornLoadingSpinner';

interface CandidatesPanelProps {
  job: IJobDetail;
  panelHeight?: number;
}

export default function CandidatesPanel({
  job,
  panelHeight,
}: CandidatesPanelProps) {
  useJobDetailUrlSync();

  const candidateTabs = CANDIDATE_PANEL_TABS.filter(
    (tab) => tab.group === 'candidates'
  );
  const outcomeTabs = CANDIDATE_PANEL_TABS.filter(
    (tab) => tab.group !== 'candidates'
  );

  const activeView = useJobDetailStore((state) => state.activeTab);
  const setActiveView = useJobDetailStore((state) => state.setActiveTab);
  const activeTab =
    CANDIDATE_PANEL_TABS.find((tab) => tab.value === activeView) ??
    CANDIDATE_PANEL_TABS[0];
  const activeGroup = activeTab.group;

  const candidateFilters = useActiveCandidateFilters();

  // One query for the active tab only.
  const {
    candidates,
    remaining,
    restrictedAccess,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useCandidatesQuery(job.id, activeView, candidateFilters);

  const { data: counters } = useCandidateCounters(job.id);

  const suggestedPaywall =
    activeView === CandidateStage.SUGGESTED && restrictedAccess && job.isActive;
  const suggestedLocation =
    [job.address?.line1, job.address?.city].filter(Boolean).join(', ') ||
    job.address?.googleAddress ||
    '';

  const tabLabel = (tab: CandidatePanelTab) => {
    const count = candidatePanelTabCount(tab.value, counters);
    const label = capitalizeValue(tab.value);
    return count != null ? `${label} (${count})` : label;
  };

  const navItems: CollapsibleNavItem[] = [
    {
      value: 'candidates',
      label: 'Candidates',
      children: candidateTabs.map((tab) => ({
        value: tab.value,
        label: tabLabel(tab),
        icon: <tab.Icon size={20} />,
      })),
    },
    ...outcomeTabs.map((tab) => ({
      value: tab.value,
      label: capitalizeValue(tab.value),
    })),
  ];

  const selectGroup = (group: string | null) => {
    const first = CANDIDATE_PANEL_TABS.find((tab) => tab.group === group);
    if (first) setActiveView(first.value);
  };

  const emptyList = (tab: CandidatePanelTab) => {
    const { emptyLabel, emptyDescription } = resolveCandidatePanelEmpty(
      tab,
      job.isActive,
      tab.value === CandidateStage.SUGGESTED && restrictedAccess
    );
    return (
      <CandidatesEmptyList
        Icon={tab.Icon}
        label={emptyLabel}
        description={emptyDescription}
      />
    );
  };

  const renderContent = (tab: CandidatePanelTab) => {
    if (tab.value !== activeView) {
      return emptyList(tab);
    }
    if (isLoading) {
      return (
        <Center mih={200} flex={1}>
          <BullhornLoadingSpinner fade />
        </Center>
      );
    }
    if (suggestedPaywall) {
      return (
        <Box flex={1} mih={0} h="100%" style={{ overflowY: 'auto' }}>
          <SuggestedWorkersPaywall
            count={counters?.suggestedWorkers ?? 0}
            locationLabel={suggestedLocation}
          />
        </Box>
      );
    }
    return candidates.length ? (
      <Stack gap="sm" mt="sm" flex={1} mih={0} style={{ overflow: 'hidden' }}>
        <Box flex={1} mih={0} style={{ overflowY: 'auto' }}>
          <Stack gap="sm">
            {candidates.map((candidate) => (
              <CandidateCard
                key={candidate.id}
                candidate={candidate}
                jobId={job.id}
              />
            ))}
          </Stack>
        </Box>
        {(hasNextPage || isFetchingNextPage) && (
          <Button
            variant="light"
            fullWidth
            onClick={() => fetchNextPage()}
            loading={isFetchingNextPage}
            disabled={!hasNextPage}
          >
            Load more ({remaining})
          </Button>
        )}
      </Stack>
    ) : (
      <Center flex={1}>{emptyList(tab)}</Center>
    );
  };

  const mobileLayout = (
    <Stack gap="md" mt={10}>
      <CollapsibleNav
        items={navItems}
        value={activeView}
        onChange={(value) => setActiveView(value as CandidateStage)}
      />
      {!suggestedPaywall && <CandidatesFilterBar />}
      <Box>{renderContent(activeTab)}</Box>
    </Stack>
  );

  const desktopLayout = (
    <Tabs
      value={activeGroup}
      onChange={selectGroup}
      h="100%"
      display="flex"
      style={{ flexDirection: 'column' }}
    >
      <Tabs.List w="100%" grow>
        <Tabs.Tab value="candidates">
          Candidates ({candidatesGroupCount(counters)})
        </Tabs.Tab>
        <Tabs.Tab value="offered">
          Offered ({counters?.jobOffers ?? 0})
        </Tabs.Tab>
        <Tabs.Tab value="hired">
          Hired ({counters?.hiredAssignments ?? 0}/{job.totalPositions})
        </Tabs.Tab>
      </Tabs.List>

      <Tabs.Panel
        value="candidates"
        flex={1}
        mih={0}
        p={0}
        style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
      >
        <Tabs
          value={activeView}
          onChange={(value) => value && setActiveView(value as CandidateStage)}
          variant="pills"
          mt={10}
          display="flex"
          style={{ flexDirection: 'column', flex: 1, minHeight: 0 }}
        >
          <Tabs.List w="100%" grow fw={400} style={{ flexWrap: 'nowrap' }}>
            {candidateTabs.map((tab) => (
              <Tabs.Tab key={tab.value} value={tab.value} c="var(--black)">
                <Group justify="center" wrap="nowrap" gap={6}>
                  <tab.Icon size={20} />
                  {tabLabel(tab)}
                </Group>
              </Tabs.Tab>
            ))}
          </Tabs.List>

          {!suggestedPaywall && <CandidatesFilterBar />}
          {candidateTabs.map((tab) => (
            <Tabs.Panel
              key={tab.value}
              value={tab.value}
              p={0}
              flex={1}
              mih={0}
              style={{
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {renderContent(tab)}
            </Tabs.Panel>
          ))}
        </Tabs>
      </Tabs.Panel>

      {outcomeTabs.map((tab) => (
        <Tabs.Panel
          key={tab.value}
          value={tab.value}
          flex={1}
          mih={0}
          p={0}
          style={{
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Stack flex={1} mih={0} style={{ overflow: 'hidden' }}>
            <CandidatesFilterBar />
            {renderContent(tab)}
          </Stack>
        </Tabs.Panel>
      ))}
    </Tabs>
  );

  const body = isMobile ? mobileLayout : desktopLayout;

  return (
    <Paper
      p={16}
      mb={20}
      h={panelHeight}
      display="flex"
      style={{ flexDirection: 'column' }}
    >
      <Text tt="uppercase" fz={14} fw={700}>
        {job.title.name}
      </Text>
      {panelHeight ? (
        <Box
          flex={1}
          mih={0}
          display="flex"
          style={{ flexDirection: 'column', overflow: 'hidden' }}
        >
          {body}
        </Box>
      ) : (
        body
      )}
    </Paper>
  );
}
